import { useMutation, useQuery } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import {
  ApplicationStatus,
  AnimalScreeningReportVendors,
  Verification,
  VerificationNames
} from "shared/types";
import { Applicant } from "types/application";
import { User03 } from "untitledui-js-base";
import PetsIcon from "../../../../shared/icons/pets.svg?react";
import { ReportHeader } from "views/applications-id-report/components/ReportHeader";
import { Section } from "views/applications-id-report/components/Section";
import { DynamicKeyValue } from "views/applications-id-report/components/SmartRender/DynamicKeyValue";
import { OurPetPolicyReportFields } from "./components/OurPetPolicyReportFields";
import { PetScreeningReportFields } from "./components/PetScreeningReportFields";
import { has } from "shared/utils";
import { useMemo } from "react";
import { Loader } from "shared/components/Loader";
import { EmailSentSection } from "./components/EmailSentSection";
import { ErrorSection } from "./components/ErrorSection";
import api from "api/landlord/applications";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import { VerificationReport } from "types/landlord/reports";
import { AxiosError } from "axios";
import { PetScreeningReport } from "./types/index";
import { PetScreeningAnimal } from "./types/pet-screening";
import { OurPetPolicyAnimal } from "./types/our-pet-policy";
import { NoPets } from "./slices/NoPets";
import { AnimalScreeningStats } from "./slices/AnimalScreeningStats";

interface PetScreeningReportSliceProps {
  verification?: Verification;
  applicant: Applicant;
  applicationStatus: ApplicationStatus;
}

export const PetScreeningReportSlice = ({
  verification,
  applicant,
  applicationStatus
}: PetScreeningReportSliceProps) => {
  const { id: verificationId } = verification || {};
  const {
    data: verificationData,
    isLoading,
    error,
    refetch
  } = useQuery<VerificationReport<PetScreeningReport>, AxiosError>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.PetScreeningReport,
      applicationId: applicant.application_id,
      applicantId: applicant.id,
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          verificationId || 0
        )
        .then((data) => data.verification)
  });

  const {
    isPending,
    mutate,
    error: mutationError
  } = useMutation({
    mutationKey: ["PET_SCREENING_REPORT", verificationId],
    mutationFn: () => {
      if (vendor === AnimalScreeningReportVendors.PetScreening)
        return api
          .getApplicantVerification(
            applicant.application_id,
            applicant.id,
            verificationId || 0
          )
          .then((data) => data.verification);
      return addressApi.getManualAnimalScreeningReport(verificationId || 0);
    },
    onSuccess: () => {
      refetch();
    }
  });

  const { report, requested_at, updated_at, vendor } = verificationData || {};

  const hasReportData: boolean = useMemo(
    () => Boolean(report && has(report.applicant_info)),
    [report]
  );

  const waitingForReport = useMemo(() => {
    return Boolean(requested_at && !hasReportData);
  }, [requested_at, hasReportData, error, mutationError]);

  if (isLoading || isPending)
    return (
      <>
        <ReportHeader
          applicationStatus={applicationStatus}
          applicationId={applicant.application_id}
          applicantId={applicant.id}
          name={VerificationNames.PetScreeningReport}
          title="Animal screening"
          onRerunReport={mutate}
          reRunReportButtonText=""
          reRunLoading={isPending}
          reportDateText="Last updated on"
          reportDate={updated_at}
          hidePrint
        />
        <Loader />
      </>
    );

  if (error)
    return (
      <>
        <ReportHeader
          applicationStatus={applicationStatus}
          applicationId={applicant.application_id}
          applicantId={applicant.id}
          name={VerificationNames.PetScreeningReport}
          title="Animal screening"
          onRerunReport={mutate}
          reRunReportButtonText=""
          reRunLoading={isPending}
          reportDateText="Last updated on"
          reportDate={updated_at}
          hidePrint
        />
        {error.code === "400" ? (
          // 400 represents the fact that verification is not YET present on
          // our backend
          <EmailSentSection applicant={applicant} emailSentAt={requested_at} />
        ) : (
          <ErrorSection
            vendor={vendor as AnimalScreeningReportVendors}
            errorMessage={
              verificationData?.error_details?.body?.message ||
              verificationData?.error ||
              mutationError?.message
            }
          />
        )}
      </>
    );

  if (waitingForReport)
    return (
      <>
        <ReportHeader
          applicationStatus={applicationStatus}
          applicationId={applicant.application_id}
          applicantId={applicant.id}
          name={VerificationNames.PetScreeningReport}
          title="Animal screening"
          onRerunReport={mutate}
          reRunReportButtonText=""
          reRunLoading={isPending}
          reportDateText="Last updated on"
          reportDate={updated_at}
          hidePrint
        />
        <EmailSentSection applicant={applicant} emailSentAt={requested_at} />
      </>
    );

  if (!verificationData) return null;

  return (
    <>
      <ReportHeader
        applicationStatus={applicationStatus}
        applicationId={applicant.application_id}
        applicantId={applicant.id}
        name={VerificationNames.PetScreeningReport}
        title="Animal screening"
        onRerunReport={mutate}
        reRunReportButtonText=""
        reRunLoading={isPending}
        reportDateText="Last updated on"
        reportDate={updated_at}
        hidePrint
      />

      <AnimalScreeningStats
        pets={report?.pets}
        vendor={vendor as AnimalScreeningReportVendors}
        detailsPageUrl={report?.applicant_info?.details_page_url}
      />

      {!report?.pets?.length ? (
        <NoPets
          isPetScreeningVendor={
            vendor === AnimalScreeningReportVendors.PetScreening
          }
        />
      ) : (
        <>
          <Section
            sectionTitle="Applicant info"
            maxWidth="100%"
            icon={<User03 className="stroke-content-400" size="20px" />}
          >
            <DynamicKeyValue
              detailsKey="Name"
              value={`${applicant.first_name} ${applicant.last_name}`}
            />
            <DynamicKeyValue detailsKey="Email" value={applicant.email} />
            <DynamicKeyValue
              detailsKey="Property manager ID"
              value={
                vendor === AnimalScreeningReportVendors.PetScreening
                  ? "--"
                  : verificationData.schema?.settings.settings
                      ?.property_management_id || ""
              }
            />
          </Section>
          <Section
            sectionTitle="Pets / Assistance animals"
            maxWidth="100%"
            icon={<PetsIcon className="size-5 stroke-content-500" />}
          >
            {vendor === AnimalScreeningReportVendors.PetScreening ? (
              <PetScreeningReportFields
                animals={report.pets as PetScreeningAnimal[]}
              />
            ) : (
              <OurPetPolicyReportFields
                animals={report.pets as OurPetPolicyAnimal[]}
              />
            )}
          </Section>
        </>
      )}
    </>
  );
};
